/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * SettingsPositions component
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @version 1.23.0
 * @since 1.3.0
 */

import { memo, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

import debounce from 'lodash/debounce';
import { connect } from 'react-redux';
import { compose, Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import InfinityScrollWrapper from '@components/InfinityScrollWrapper/InfinityScrollWrapper';
import { LabelEditorList } from '@components/Table/components/footerModal/LabelEditor/components/LabelEditorList';
import LabelEditorSearch from '@components/Table/components/footerModal/LabelEditor/components/LabelEditorSearch';
import { DEBOUNCE_DEFAULT_DELAY } from '@modules/app/appConstants';
import { makeSelectTranslationPageSettings } from '@modules/internationalization/intSelectors';
import type { IPageSettingsTranslation } from '@modules/internationalization/types/pages/pageSettings';
import type { IRootReducer } from '@reducers/rootReducer';

import {
  SettingsLabelsContentStyled,
  SettingsLabelsListStyled,
} from '../../SettingsLabels/components/styled/SettingsLabels';
import {
  PageSettingsContentStyled,
  PageSettingsHeaderStyled,
} from '../../styled/PageSettingsStyled';
import {
  addSettingsPositionAction,
  getSettingsPositionsListAction,
  removeSettingsPositionAction,
} from '../settingsPositionsActions';
import {
  makeSelectSettingsPositionsIsLoading,
  makeSelectSettingsPositionsList,
  makeSelectSettingsPositionsListHasMore,
} from '../settingsPositionsSelectors';
import type {
  IGetSettingsPositionsListActionPayload,
  ISettingsPositionsItem,
} from '../settingsPositionsTypes';

type TSettingsPositionsProps = IConnectedProps & IDispatchProps;

const SettingsPositions = (props: TSettingsPositionsProps) => {
  const [searchText, setSearchText] = useState('');
  const listBodyRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

  const loadList = debounce((search: string, start?: boolean) => {
    props.getPositionsList({
      search,
      start: start ?? false,
    });
  }, DEBOUNCE_DEFAULT_DELAY);

  useEffect(() => {
    loadList(searchText, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return (
    <PageSettingsContentStyled>
      <SettingsLabelsContentStyled data-module="settingsPositions">
        <PageSettingsHeaderStyled>
          {props.settingsTranslation.settingsPositions.title}
        </PageSettingsHeaderStyled>

        <LabelEditorSearch
          createLabel={props.addPosition}
          isLoading={props.isLoading}
          searchLabel={setSearchText}
          searchValue={searchText}
          translation={props.settingsTranslation.settingsPositions.labelEditor}
        />

        <InfinityScrollWrapper
          hasMore={props.hasMore}
          sizeElement={listBodyRef}
          watchDocumentBody={true}
          onScrollEvent={() => loadList(searchText)}
        >
          <SettingsLabelsListStyled ref={listBodyRef}>
            <LabelEditorList
              isLoading={props.isLoading}
              labels={props.positionsList}
              removeLabel={props.removePosition}
              searchValue={''}
              translation={
                props.settingsTranslation.settingsPositions.labelEditor
              }
            />
          </SettingsLabelsListStyled>
        </InfinityScrollWrapper>
      </SettingsLabelsContentStyled>
    </PageSettingsContentStyled>
  );
};

export interface IConnectedProps {
  isLoading: boolean;
  hasMore: boolean;
  positionsList: ISettingsPositionsItem[];
  settingsTranslation: IPageSettingsTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>(
  {
    isLoading: makeSelectSettingsPositionsIsLoading(),
    hasMore: makeSelectSettingsPositionsListHasMore(),
    positionsList: makeSelectSettingsPositionsList(),
    settingsTranslation: makeSelectTranslationPageSettings(),
  },
);

interface IDispatchProps {
  addPosition: (n: string) => void;
  getPositionsList: (payload: IGetSettingsPositionsListActionPayload) => void;
  removePosition: (n: number) => void;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  addPosition: (payload) => dispatch(addSettingsPositionAction(payload)),
  getPositionsList: (payload) =>
    dispatch(getSettingsPositionsListAction(payload)),
  removePosition: (payload) => dispatch(removeSettingsPositionAction(payload)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(memo(SettingsPositions));
